/**
 * PageViewer.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from './ToastContainer';
import { useAuth } from '../context/AuthContext';
import { getIcon } from '../utils/icons';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import ShareFloatingButton from './ShareFloatingButton';
import ShareModal from './ShareModal';
import SEO from './SEO';
import TunePage from './TunePage';
import TuneFormatMarker from './TuneFormatMarker';
import UserContentFeedback from './UserContentFeedback';
import { resolveCuratedItemsForViewer } from '../utils/resolveCuratedItems';
import { normalizeTuneSourceUrl } from '../utils/normalizeTuneSourceUrl';
import {
  buildTuneSeoDescription,
  buildTuneSeoKeywords,
  resolveTuneOgImageUrl,
} from '../utils/tunePageSeo';
import { isEmbeddedArticlePageHtml } from '../utils/embeddedPageHtml';
import { getProductImage } from '../utils/imageMapping';
import { getAuthHeaders } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import { getTranslatedPage } from '../i18n/pages';
import './PageViewer.css';

/** First absolute http(s) URL from common legacy metadata keys (prefers source_url). */
function firstAbsoluteLegacySourceUrl(meta) {
  if (!meta || typeof meta !== 'object') return null;
  for (const k of ['source_url', 'source_reference', 'source', 'publicationSource']) {
    const v = meta[k];
    if (v == null || !String(v).trim()) continue;
    const n = normalizeTuneSourceUrl(String(v).trim());
    if (/^https?:\/\//i.test(n)) return n;
  }
  return null;
}

/** Lowercase, drop punctuation, collapse whitespace — for book index ↔ tune title matching. */
function normalizeBookIndexTuneKey(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Alternate spellings for index lines like "Quickstep, The" vs DB "The Athole Volunteers Quickstep".
 */
function bookIndexTitleLookupKeys(title) {
  const n = normalizeBookIndexTuneKey(title.replace(/\n/g, ' '));
  if (!n) return [];
  const keys = new Set([n]);
  const commaThe = n.match(/^(.+),\s*the$/);
  if (commaThe && commaThe[1]) {
    keys.add(`the ${commaThe[1]}`.trim());
  }
  if (n.startsWith('the ')) {
    keys.add(`${n.slice(4).trim()}, the`);
  }
  return [...keys].filter(Boolean);
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Re-insert scripts under `.page-content` so they execute (React innerHTML does not run them).
 * - External scripts: await `load` before the next tag (so Leaflet loads before `L.map`).
 * - Combined `<script src="…"> inline </script>`: browsers ignore inline when `src` is set;
 *   we load the URL first, then inject the inline body as a sibling script.
 * - If every inline-only script appears before every external script (common paste mistake),
 *   we run all externals first, then all inlines (does not change correctly interleaved HTML).
 */
function copyScriptAttributes(from, to) {
  Array.from(from.attributes).forEach((attr) => {
    if (attr.name === 'data-pageviewer-exec') return;
    to.setAttribute(attr.name, attr.value);
  });
}

/** When all inline-only tags precede all `src` tags, run externals first so `L` etc. exist. */
function getScriptsExecutionOrder(scripts) {
  const hasSrc = (s) => !!(s.getAttribute('src') || '').trim();
  const hasInline = (s) => !!(s.textContent || '').trim();
  const idxSrc = [];
  const idxInlineOnly = [];
  scripts.forEach((s, i) => {
    if (hasSrc(s)) idxSrc.push(i);
    else if (hasInline(s)) idxInlineOnly.push(i);
  });
  if (!idxSrc.length || !idxInlineOnly.length) return scripts;
  const maxInline = Math.max(...idxInlineOnly);
  const minSrc = Math.min(...idxSrc);
  if (maxInline < minSrc) {
    const srcScripts = idxSrc.sort((a, b) => a - b).map((i) => scripts[i]);
    const inlineScripts = idxInlineOnly.sort((a, b) => a - b).map((i) => scripts[i]);
    return [...srcScripts, ...inlineScripts];
  }
  return scripts;
}

function runEmbeddedPageScriptsSequentially(containerEl) {
  let scripts = Array.from(
    containerEl.querySelectorAll('script:not([data-pageviewer-exec])')
  );
  scripts = getScriptsExecutionOrder(scripts);
  return scripts.reduce(
    (chain, oldScript) => chain.then(() => replaceScriptAndWait(oldScript)),
    Promise.resolve()
  );
}

function replaceScriptAndWait(oldScript) {
  const parent = oldScript.parentNode;
  if (!parent) return Promise.resolve();

  const src = (oldScript.getAttribute('src') || '').trim();
  const inline = oldScript.textContent || '';
  const inlineTrim = inline.trim();

  if (src && inlineTrim) {
    return new Promise((resolve) => {
      const ext = document.createElement('script');
      copyScriptAttributes(oldScript, ext);
      ext.setAttribute('data-pageviewer-exec', '1');
      ext.onload = () => {
        const inlineEl = document.createElement('script');
        inlineEl.setAttribute('data-pageviewer-exec', '1');
        inlineEl.appendChild(document.createTextNode(inline));
        if (ext.nextSibling) {
          parent.insertBefore(inlineEl, ext.nextSibling);
        } else {
          parent.appendChild(inlineEl);
        }
        resolve();
      };
      ext.onerror = () => {
        console.error('[PageViewer] Failed to load script:', src);
        resolve();
      };
      parent.replaceChild(ext, oldScript);
    });
  }

  if (src) {
    return new Promise((resolve) => {
      const newScript = document.createElement('script');
      copyScriptAttributes(oldScript, newScript);
      newScript.setAttribute('data-pageviewer-exec', '1');
      newScript.onload = () => resolve();
      newScript.onerror = () => {
        console.error('[PageViewer] Failed to load script:', src);
        resolve();
      };
      parent.replaceChild(newScript, oldScript);
    });
  }

  const newScript = document.createElement('script');
  copyScriptAttributes(oldScript, newScript);
  newScript.setAttribute('data-pageviewer-exec', '1');
  newScript.appendChild(document.createTextNode(oldScript.textContent));
  parent.replaceChild(newScript, oldScript);
  return Promise.resolve();
}

/**
 * Legacy book index lines often use long hyphen runs before the tune type (e.g. "Tune name ----- reel").
 * Strip those and optional trailing duplicate type when we already show it in .tune-type.
 */
function sanitizeBookIndexNameForDisplay(raw, tuneTypeLabel) {
  let s = String(raw || '').replace(/\r?\n/g, ' ');
  s = s.replace(/[\u00a0\s]*(?:[-–—_]\s*){2,}/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  const typeNorm = String(tuneTypeLabel || '').trim();
  if (typeNorm) {
    const parts = typeNorm.split(/\s+/).map(escapeRegExp);
    const tail = `\\s+['"]?${parts.join("\\s+")}['"]?\\s*$`;
    const reTail = new RegExp(tail, 'i');
    if (reTail.test(s)) s = s.replace(reTail, '').trim();
    const reEllipsis = new RegExp(
      `\\s*(?:\\.{3}|…)\\s*['"]?${parts.join("\\s+")}['"]?\\s*$`,
      'i'
    );
    s = s.replace(reEllipsis, '').trim();
    const reParen = new RegExp(
      `\\(\\s*['"]?${parts.join("\\s+")}['"]?\\s*\\)\\s*$`,
      'i'
    );
    s = s.replace(reParen, '').trim();
  }
  return s;
}

/** Respect Page Editor related_content.sections + section_limits on the unified Related grid (pages without tune-page template). */
function applyRelatedContentSectionModes(items, explicitRelated) {
  const sections = explicitRelated.sections;
  if (!sections || typeof sections !== 'object') return items;

  const limits = explicitRelated.section_limits || {};
  const cap = (k, def) => {
    const n = parseInt(limits[k], 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(100, n) : def;
  };

  const hasTune = Object.prototype.hasOwnProperty.call(sections, 'tunes');
  const hasArt = Object.prototype.hasOwnProperty.call(sections, 'articles');
  const hasProd = Object.prototype.hasOwnProperty.call(sections, 'products');
  const hasCurated = Object.prototype.hasOwnProperty.call(sections, 'curated');
  const curatedSectionOn = hasCurated && sections.curated !== 'off';

  if (!hasTune && !hasArt && !hasProd && !curatedSectionOn) return items;

  const tunesMode = hasTune ? sections.tunes : null;
  const articlesMode = hasArt ? sections.articles : null;
  const productsMode = hasProd ? sections.products : null;

  const maxT = cap('tunes', 12);
  const maxA = cap('articles', 8);
  const maxP = cap('products', 24);
  const maxC = cap('curated', 24);
  let nCurated = 0;

  const isTune = (it) =>
    it.itemType === 'tune' ||
    it.pageType === 'tune-notation' ||
    it.pageType === 'abc-notation' ||
    it.page_type === 'tune-notation' ||
    it.page_type === 'abc-notation';
  const isProduct = (it) => it.itemType === 'product';
  const isArticle = (it) => !isTune(it) && !isProduct(it);

  let nT = 0;
  let nA = 0;
  let nP = 0;
  const out = [];

  for (const it of items) {
    if (it.fromCurated) {
      if (!curatedSectionOn) continue;
      if (nCurated >= maxC) continue;
      nCurated += 1;
      out.push(it);
      continue;
    }

    const fe = !!it.fromExplicit;
    if (isTune(it)) {
      if (tunesMode == null) {
        out.push(it);
        continue;
      }
      if (tunesMode === 'off') continue;
      if (tunesMode === 'explicit' && !fe) continue;
      if (tunesMode === 'tags' && fe) continue;
      if (nT >= maxT) continue;
      nT += 1;
      out.push(it);
      continue;
    }
    if (isProduct(it)) {
      if (productsMode == null) {
        out.push(it);
        continue;
      }
      if (productsMode === 'off') continue;
      if (productsMode === 'explicit' && !fe) continue;
      if (nP >= maxP) continue;
      nP += 1;
      out.push(it);
      continue;
    }
    if (isArticle(it)) {
      if (articlesMode == null) {
        out.push(it);
        continue;
      }
      if (articlesMode === 'off') continue;
      if (articlesMode === 'explicit' && !fe) continue;
      if (articlesMode === 'tags' && fe) continue;
      if (nA >= maxA) continue;
      nA += 1;
      out.push(it);
      continue;
    }
    out.push(it);
  }

  return out;
}

/** IDs from article metadata: linked product picker + product_id / product_ids fields. */
function parseLinkedProductIds(parsedMeta) {
  if (!parsedMeta || typeof parsedMeta !== 'object') return [];
  const raw = [];
  const add = (v) => {
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'number' && Number.isFinite(v)) {
      raw.push(String(Math.trunc(v)));
      return;
    }
    const s = String(v).trim();
    if (!s) return;
    if (s.includes(',')) {
      s.split(',').forEach((p) => {
        const t = p.trim();
        if (t) raw.push(t);
      });
    } else {
      raw.push(s);
    }
  };
  add(parsedMeta.productId);
  add(parsedMeta.relatedProductId);
  add(parsedMeta.product_id);
  add(parsedMeta.product_ids);
  const seen = new Set();
  const out = [];
  for (const id of raw) {
    const k = String(id).toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(id);
    }
  }
  return out;
}

// Helper component for related items to handle image state
const RelatedItemCard = ({ item, navigate, canEdit, onUnrelate }) => {
  const [imageError, setImageError] = useState(false);
  const isProduct = item.itemType === 'product';
  const productImageSrc = isProduct ? getProductImage(item) : null;
  const contentImageSrc =
    !isProduct && item.image
      ? item.image.startsWith('http') || item.image.startsWith('/')
        ? item.image
        : null
      : null;
  const imageSrc = isProduct ? productImageSrc : contentImageSrc;
  const showImage = !!imageSrc && !imageError;

  const handleClick = (e) => {
    // Prevent navigation if clicking the unrelate button
    if (e.target.closest('.unrelate-btn')) return;

    if (item.itemType === 'product') {
      navigate(`/product/${item.id}`);
    } else if (item.itemType === 'tune-asset' && item.tuneAssetId != null) {
      navigate(`/tune-asset/${item.tuneAssetId}`);
    } else if (item.itemType === 'route' && item.routeHref) {
      navigate(item.routeHref);
    } else {
      const pid = item.page_id || item.pageId;
      if (pid) navigate(`/page/${pid}`);
    }
  };

  const showImageBlock = isProduct || !!contentImageSrc;
  const displayName = item.name || item.title || '';
  const isTuneItem = item.itemType === 'tune' || item.itemType === 'tune-asset';

  if (isProduct) {
    return (
      <div className="related-product-card related-product-card--cover-only" onClick={handleClick}>
        {canEdit && (
          <button
            className="unrelate-btn"
            onClick={() => onUnrelate(item)}
            title="Remove this relationship"
          >
            {getIcon('close')}
          </button>
        )}
        <div
          className="related-product-link related-product-link--cover-only"
          title={displayName}
          aria-label={displayName ? `View product: ${displayName}` : 'View product'}
        >
          <div className={`related-product-image${showImage ? '' : ' related-product-image--empty'}`}>
            {showImage ? (
              <img
                src={imageSrc}
                alt=""
                loading="lazy"
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="related-product-fallback" aria-hidden="true">📚</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="related-product-card related-product-card--content" onClick={handleClick}>
      {showImageBlock && (
        <div className="related-product-image">
          {showImage ? (
            <img
              src={imageSrc}
              alt={displayName}
              loading="lazy"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="placeholder-image" aria-hidden="true">📚</span>
          )}
        </div>
      )}
      <div className="related-product-info">
        {isTuneItem ? (
          <div className="related-item-title-row">
            <h4>{displayName}</h4>
            <TuneFormatMarker item={item} variant="inline" />
          </div>
        ) : (
          <>
            <div className="related-item-title-row">
              <div className="item-type-badge" style={{
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                color: 'var(--color-primary)',
                marginBottom: '0.25rem',
                fontWeight: 'bold'
              }}>
                {item.itemType}
              </div>
            </div>
            <h4>{displayName}</h4>
          </>
        )}
        {canEdit && (
          <button
            type="button"
            className="unrelate-btn unrelate-btn--stacked"
            onClick={() => onUnrelate(item)}
            title="Remove this relationship"
          >
            {getIcon('close')}
          </button>
        )}
        <p className="artist" style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
          {item.description ? `${item.description.substring(0, 50)}...` : 'Read now'}
        </p>
      </div>
    </div>
  );
};

const PageViewer = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useToast();
  const { currentUser, hasAnyRole } = useAuth();
  const { language, t } = useLanguage();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState({
    author: null,
    tags: [],
    year: null,
    productId: null,
    productLink: null,
    source: null,
    sourceLinkUrl: null,
  });
  const [showShareModal, setShowShareModal] = useState(false);

  // Determine the actual pageId - handle both /page/:pageId and direct footer page routes
  const getActualPageId = () => {
    if (pageId) {
      return pageId; // If pageId is provided via /page/:pageId route
    }

    // Map direct footer page routes to their pageIds
    const pathToPageId = {
      '/privacy-policy': 'privacy-policy',
      '/terms-of-use': 'terms-of-use',
      '/return-policy': 'return-policy',
      '/shipping-policy': 'shipping-policy',
      '/faq': 'faq'
    };

    return pathToPageId[location.pathname] || null;
  };

  const actualPageId = getActualPageId();

  // Navigating between /page/:id reuses this component; scroll would stay at the Related section.
  useEffect(() => {
    if (actualPageId) window.scrollTo(0, 0);
  }, [actualPageId]);

  // Check if user can edit pages
  // Handle both role formats: array of strings or array of objects with name property
  const canEdit = currentUser && currentUser.roles && (
    // Check if roles is array of strings
    (Array.isArray(currentUser.roles) && typeof currentUser.roles[0] === 'string' &&
      ['admin', 'super_admin', 'content_creator'].some(role => currentUser.roles.includes(role))) ||
    // Check if roles is array of objects with name property
    (hasAnyRole && hasAnyRole(['admin', 'super_admin', 'content_creator']))
  );

  useEffect(() => {
    loadPage();
  }, [actualPageId, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle script execution for legacy templates (not tune pages — those render via TunePage.js)
  useEffect(() => {
    let observer = null;
    const isTunePage = pageData && (pageData.pageType === 'tune-notation' || pageData.pageType === 'abc-notation');
    if (pageData && pageData.html && !isTunePage) {
      // Only execute scripts if the page has embedded scripts
      if (pageData.html.includes('<script')) {
        const pageContentEl = document.querySelector('.page-content');
        if (pageContentEl) {
          runEmbeddedPageScriptsSequentially(pageContentEl).catch((err) => {
            console.error('[PageViewer] Embedded script sequence failed:', err);
          });
        }
      }

      // Setup a MutationObserver to hide empty related sections generated by legacy templates
      const pageContent = document.querySelector('.page-content');
      if (pageContent) {
        const hideEmptySections = () => {
          document.querySelectorAll('.page-content .related-placeholder').forEach(placeholder => {
            // Do not hide sections that are currently 'Loading...'
            if (placeholder.textContent && placeholder.textContent.toLowerCase().includes('loading')) {
              return;
            }
            const section = placeholder.closest('.related-products-section, .related-products, .related-tunes, .related-articles');
            if (section) {
              section.style.display = 'none';
            }
          });
        };
        
        // Initial check
        hideEmptySections();

        observer = new MutationObserver(() => {
          hideEmptySections();
        });
        
        observer.observe(pageContent, { childList: true, subtree: true });
      }
    }
    
    return () => {
      if (observer) observer.disconnect();
    };
  }, [pageData]);

  // Hydrate Book Index with available tunes
  useEffect(() => {
    const hydrateBookIndex = async () => {
      if (!pageData || pageData.pageType !== 'book-index' || !pageData.html) return;

      try {
        // Fetch list of all available tunes
        const response = await fetch('/api/content/tunes');
        if (!response.ok) return;

        const allTunes = await response.json();

        /** Only real tune pages — /api/content/tunes also lists book/cd index pages. */
        const tuneRows = allTunes.filter(
          (t) => t.page_type === 'tune-notation' || t.page_type === 'abc-notation'
        );

        const tuneMap = new Map();
        const tuneEntries = [];
        const tuneTypeByPageId = new Map();
        tuneRows.forEach((tune) => {
          if (!tune.title) return;
          const flat = normalizeBookIndexTuneKey(tune.title.replace(/\n/g, ' '));
          bookIndexTitleLookupKeys(flat).forEach((k) => {
            if (k && !tuneMap.has(k)) tuneMap.set(k, tune.page_id);
          });
          tuneEntries.push({ norm: flat, pageId: tune.page_id });
          try {
            const meta =
              typeof tune.metadata === 'string'
                ? JSON.parse(tune.metadata || '{}')
                : tune.metadata || {};
            const tt = meta.tune_type != null ? String(meta.tune_type).trim() : '';
            if (tt) tuneTypeByPageId.set(tune.page_id, tt);
          } catch (_) {
            /* ignore */
          }
        });

        let bookIndexTypes = {};
        try {
          const rawMeta =
            typeof pageData.metadata === 'string'
              ? JSON.parse(pageData.metadata || '{}')
              : pageData.metadata || {};
          if (rawMeta.book_index_types && typeof rawMeta.book_index_types === 'object') {
            bookIndexTypes = rawMeta.book_index_types;
          }
        } catch (_) {
          /* ignore */
        }

        const resolveBookIndexTuneType = (rawName, pageId) => {
          const cleaned = String(rawName || '').replace(/\n/g, ' ');
          for (const k of bookIndexTitleLookupKeys(cleaned)) {
            const v = bookIndexTypes[k];
            if (v != null && String(v).trim()) return String(v).trim();
          }
          const flat = normalizeBookIndexTuneKey(cleaned);
          const v2 = bookIndexTypes[flat];
          if (v2 != null && String(v2).trim()) return String(v2).trim();
          if (pageId && tuneTypeByPageId.has(pageId)) return tuneTypeByPageId.get(pageId);
          return '';
        };

        const ensureTuneTypeInRow = (item, typeStr) => {
          if (!typeStr || item.querySelector('.tune-index-right')) return;
          const pageSpan = item.querySelector('.tune-page');
          const nameSpan = item.querySelector('.tune-name');
          if (!pageSpan || !nameSpan) return;
          const right = document.createElement('span');
          right.className = 'tune-index-right';
          const typeSpan = document.createElement('span');
          typeSpan.className = 'tune-type';
          typeSpan.textContent = typeStr;
          pageSpan.remove();
          right.appendChild(typeSpan);
          right.appendChild(pageSpan);
          item.appendChild(right);
        };

        const resolveTunePageIdForIndex = (rawName) => {
          const cleaned = String(rawName || '').replace(/\n/g, ' ');
          for (const k of bookIndexTitleLookupKeys(cleaned)) {
            if (tuneMap.has(k)) return tuneMap.get(k);
          }
          const n = normalizeBookIndexTuneKey(cleaned);
          if (n.length < 8) return null;
          const hits = new Set();
          for (const { norm, pageId } of tuneEntries) {
            if (!norm || norm.length < 6) continue;
            if (norm === n) {
              hits.add(pageId);
              continue;
            }
            if (norm.includes(n) || n.includes(norm)) hits.add(pageId);
          }
          if (hits.size === 1) return [...hits][0];
          return null;
        };

        // Find all index items in the DOM
        const indexItems = document.querySelectorAll('.tune-index-item');
        let availableCount = 0;

        indexItems.forEach(item => {
          const nameSpan = item.querySelector('.tune-name');
          if (!nameSpan) return;

          const nameText = nameSpan.textContent || '';
          const pageId = resolveTunePageIdForIndex(nameText);
          const tuneTypeLabel = resolveBookIndexTuneType(nameText, pageId);
          ensureTuneTypeInRow(item, tuneTypeLabel);
          const displayName = sanitizeBookIndexNameForDisplay(nameText, tuneTypeLabel);
          if (displayName) nameSpan.textContent = displayName;

          if (pageId) {
            availableCount++;

            // Update styles
            item.classList.remove('tune-unavailable');
            item.classList.add('tune-available');

            // Wrap row in link when not already linked (avoid nesting anchors)
            if (!item.querySelector('a')) {
              const anchor = document.createElement('a');
              anchor.href = `/page/${pageId}`;
              anchor.className = 'tune-index-link-wrapper';
              anchor.style.textDecoration = 'none';
              anchor.style.color = 'inherit';
              anchor.style.display = 'flex';
              anchor.style.justifyContent = 'space-between';
              anchor.style.alignItems = 'center';
              anchor.style.gap = '10px';
              anchor.style.width = '100%';

              // Move children to anchor
              while (item.firstChild) {
                anchor.appendChild(item.firstChild);
              }

              item.appendChild(anchor);
            }
          }
        });

        // Update the "X available online" and "coming soon" counts dynamically
        const successSpan = document.querySelector('.stat-success');
        if (successSpan) {
          successSpan.innerHTML = `<strong>${availableCount}</strong> available online`;
        }
        
        const totalTunes = document.querySelectorAll('.tune-index-item').length;
        const warningSpan = document.querySelector('.stat-warning');
        if (warningSpan) {
          const comingSoon = totalTunes - availableCount;
          warningSpan.innerHTML = `<strong>${comingSoon}</strong> coming soon`;
        }
        
        console.log(`Hydrated book index: ${availableCount} tunes linked out of ${totalTunes} total.`);

      } catch (err) {
        console.error('Error hydrating book index:', err);
      }
    };

    hydrateBookIndex();
  }, [pageData]);

  // Extract author from HTML content
  const extractAuthor = (html) => {
    if (!html) return null;

    // Look for "Author:" or "By:" patterns
    const authorPatterns = [
      /Author:\s*([^<\n]+)/i,
      /By:\s*([^<\n]+)/i,
      /Written by:\s*([^<\n]+)/i,
      /<strong>Author:<\/strong>\s*([^<\n]+)/i,
      /<b>Author:<\/b>\s*([^<\n]+)/i
    ];

    for (const pattern of authorPatterns) {
      const match = html.match(pattern);
      if (match && match[1]) {
        return match[1].trim().replace(/<[^>]*>/g, ''); // Remove any remaining HTML tags
      }
    }

    return 'Paul S. Cranford'; // Default if not found
  };

  // Extract source/original URL from HTML content
  const extractSource = (html) => {
    if (!html) return null;

    // Look for links that might be sources
    const sourcePatterns = [
      /Source:\s*<a href="([^"]+)"/i,
      /Original article:\s*<a href="([^"]+)"/i,
      /Originally published in:\s*<a href="([^"]+)"/i
    ];

    for (const pattern of sourcePatterns) {
      const match = html.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Generate tags from content
  const generateTags = (html, title, description, existingMetadata) => {
    // First check if there are tags in metadata
    if (existingMetadata) {
      try {
        const parsed = typeof existingMetadata === 'string' ? JSON.parse(existingMetadata) : existingMetadata;
        if (parsed.tags) {
          // If tags is a string, split by comma; if array, use as-is
          return typeof parsed.tags === 'string'
            ? parsed.tags.split(',').map(t => t.trim()).filter(t => t)
            : Array.isArray(parsed.tags) ? parsed.tags : [];
        }
      } catch (e) {
        console.error('Error parsing metadata for tags:', e);
      }
    }

    if (!html) return ['Article'];

    const tags = new Set();
    const text = html.replace(/<[^>]*>/g, ' ').toLowerCase();
    const allText = `${title} ${description} ${text}`.toLowerCase();

    // Common music/fiddle related keywords
    const keywords = [
      'fiddle', 'fiddler', 'tune', 'reel', 'jig', 'strathspey', 'waltz',
      'cape breton', 'scottish', 'irish', 'celtic', 'traditional',
      'music', 'musician', 'recording', 'album', 'cd',
      'biography', 'history', 'heritage', 'culture',
      'dance', 'dancing', 'folk', 'folk music', 'memoir', 'story',
      'bagpipe', 'piano', 'guitar', 'violin', 'accordion',
      'highland', 'gaelic', 'nova scotia', 'canada', 'scotland',
      'concert', 'performance', 'festival', 'teaching', 'composer'
    ];

    keywords.forEach(keyword => {
      if (allText.includes(keyword)) {
        // Capitalize first letter of each word
        tags.add(keyword.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
      }
    });

    // If no tags found, add a default "Article" tag
    if (tags.size === 0) {
      tags.add('Article');
      tags.add('Historical Content');
    }

    // Limit to 10 tags
    return Array.from(tags).slice(0, 10);
  };

  const loadPage = async () => {
    if (!actualPageId) {
      setLoading(false);
      showError('Invalid page ID');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/content/pages/${actualPageId}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          setPageData(null);
          showError('Page not found');
        } else {
          setPageData(null);
          showError('Failed to load page');
        }
        return;
      }

      const data = await response.json();

      // Parse metadata from JSON
      let parsedMeta = {};
      if (data.metadata) {
        try {
          parsedMeta = typeof data.metadata === 'string' ? JSON.parse(data.metadata) : data.metadata;
        } catch (e) {
          console.error('Error parsing metadata:', e);
        }
      }

      // Extract metadata - prioritize metadata fields over extracted content
      const author = parsedMeta.author || extractAuthor(data.html);
      const tags = generateTags(data.html, data.title || '', data.description || '', data.metadata);
      const source = parsedMeta.source || parsedMeta.publicationSource || extractSource(data.html); // Add source extraction

      // Get year from publicationDate in metadata, fallback to created_at
      let year = null;
      if (parsedMeta.publicationDate) {
        year = new Date(parsedMeta.publicationDate).getFullYear();
      } else if (data.created_at) {
        year = new Date(data.created_at).getFullYear();
      }

      // Fetch related products and content based on tags before setting state

      const linkedProductIds = parseLinkedProductIds(parsedMeta);

      // Primary product link for article footer (first linked id)
      const primaryLinkedId = linkedProductIds[0] != null ? linkedProductIds[0] : null;
      const productId = primaryLinkedId;
      const productLink =
        parsedMeta.productLink ||
        (primaryLinkedId != null ? `/product/${encodeURIComponent(primaryLinkedId)}` : null);

      // --- RELATED CONTENT DISCOVERY ---
      // Skip for tune pages: TunePage.js loads products/tunes/articles from tune-specific APIs.
      // (Baked HTML marker is gone after lean DB migration — do not rely on it alone.)
      const hasTuneTemplate = data.html?.includes('<!-- Start of Tune Page Content -->');
      const isTunePageType =
        data.pageType === 'tune-notation' || data.pageType === 'abc-notation';
      let relatedItems = [];
      const explicitRelated = parsedMeta.related_content || {};

      if (!hasTuneTemplate && !isTunePageType) {
        // Only run the PageViewer related-content discovery for pages that do NOT
        // use the tune-page template (which renders its own sections via inline script).
        let maxRelatedItems = (explicitRelated.max_items !== undefined && explicitRelated.max_items !== null)
          ? parseInt(explicitRelated.max_items, 10)
          : 10;
        if (!Number.isFinite(maxRelatedItems) || maxRelatedItems < 1) maxRelatedItems = 10;
        const explicitOnly = !!explicitRelated.explicit_only;
        const excludedIds = new Set((explicitRelated.excluded || []).map(e => e.id).filter(Boolean));

        const linkedProductRowsPromise =
          linkedProductIds.length === 0
            ? Promise.resolve([])
            : Promise.all(
                linkedProductIds.map((id) =>
                  fetch(`/api/products/${encodeURIComponent(id)}`)
                    .then((res) => (res.ok ? res.json() : null))
                    .catch(() => null),
                ),
              ).then((rows) =>
                rows.filter(Boolean).map((p) => ({
                  ...p,
                  itemType: 'product',
                  name: p.name,
                  match_score: 1000,
                  fromExplicit: true,
                })),
              );

        const explicitArticleIds = (explicitRelated.articles || []).map(a => a.id).filter(id => id && !excludedIds.has(id));
        const explicitTuneIds = (explicitRelated.tunes || []).map(t => t.id).filter(id => id && !excludedIds.has(id));
        const allExplicitIds = [...explicitArticleIds, ...explicitTuneIds];

        // 1a. Fetch explicit related pages (articles + tunes) by ID
        let explicitContent = [];
        if (allExplicitIds.length > 0) {
          try {
            const explicitPromises = allExplicitIds.map(id =>
              fetch(`/api/content/pages/${id}`)
                .then(res => res.ok ? res.json() : null)
                .catch(() => null)
            );
            const results = await Promise.all(explicitPromises);
            explicitContent = results.filter(r => r).map(c => ({
              ...c,
              page_id: c.pageId || c.page_id,
              itemType: (c.pageType === 'tune-notation' || c.pageType === 'abc-notation' || c.pageType === 'book-index' || c.pageType === 'cd-index') ? 'tune' : 'article',
              name: c.title,
              match_score: 999,
              fromExplicit: true
            }));
          } catch (err) {
            console.error('Error fetching explicit related items:', err);
          }
        }

        // 1b. Fetch explicit products by ID
        const explicitProductIds = (explicitRelated.products || [])
          .map(p => p.id)
          .filter(id => id && !excludedIds.has(String(id)));
        if (explicitProductIds.length > 0) {
          try {
            const productPromises = explicitProductIds.map(id =>
              fetch(`/api/products/${id}`)
                .then(res => res.ok ? res.json() : null)
                .catch(() => null)
            );
            const productResults = await Promise.all(productPromises);
            const explicitProducts = productResults.filter(r => r).map(p => ({
              ...p, itemType: 'product', name: p.name, match_score: 999, fromExplicit: true
            }));
            explicitContent = [...explicitContent, ...explicitProducts];
          } catch (err) {
            console.error('Error fetching explicit related products:', err);
          }
        }

        if (explicitRelated.sections?.curated === 'on' && Array.isArray(explicitRelated.curated) && explicitRelated.curated.length > 0) {
          try {
            const curatedRows = await resolveCuratedItemsForViewer(explicitRelated.curated, excludedIds);
            explicitContent = [...explicitContent, ...curatedRows];
          } catch (err) {
            console.error('Error resolving curated related items:', err);
          }
        }

        maxRelatedItems = Math.max(maxRelatedItems, explicitContent.length);

        // 2. Fetch tag-based related content (fill remaining slots up to maxRelatedItems)
        if (!explicitOnly && explicitContent.length < maxRelatedItems && tags && tags.length > 0) {
          try {
            const tagsQuery = tags.join(',');
            const tagFetchLimit = maxRelatedItems * 2;
            const [products, content] = await Promise.all([
              fetch(`/api/products/related?tags=${encodeURIComponent(tagsQuery)}&limit=${tagFetchLimit}`)
                .then(res => res.ok ? res.json() : []).catch(() => []),
              fetch(`/api/content/related?tags=${encodeURIComponent(tagsQuery)}&exclude=${actualPageId}&limit=${tagFetchLimit}`)
                .then(res => res.ok ? res.json() : []).catch(() => [])
            ]);
            const labeledProducts = products
              .filter(p => !excludedIds.has(String(p.id)))
              .map(p => ({
                ...p,
                itemType: 'product',
                match_score: p.match_score || 0,
                fromExplicit: false
              }));
            const existingIds = new Set(explicitContent.map(i => i.pageId || i.page_id));
            const labeledContent = content
              .filter(c => !existingIds.has(c.page_id) && !excludedIds.has(c.page_id))
              .map(c => ({
                ...c,
                page_id: c.page_id || c.pageId,
                itemType:
                  c.page_type === 'tune-notation' || c.page_type === 'abc-notation' ? 'tune' : 'article',
                name: c.title,
                image: null,
                match_score: c.match_score || 0,
                fromExplicit: false
              }));
            relatedItems = [...explicitContent, ...labeledProducts, ...labeledContent]
              .sort((a, b) => b.match_score - a.match_score)
              .slice(0, maxRelatedItems);
          } catch (err) {
            console.error('Error fetching tag-based items:', err);
            relatedItems = explicitContent;
          }
        } else {
          relatedItems = explicitContent.slice(0, maxRelatedItems);
        }

        const linkedProductRows = await linkedProductRowsPromise;
        if (linkedProductRows.length > 0) {
          const linkedSet = new Set(linkedProductRows.map((p) => String(p.id)));
          relatedItems = [
            ...linkedProductRows,
            ...relatedItems.filter(
              (item) =>
                !(
                  item.itemType === 'product' &&
                  item.id != null &&
                  linkedSet.has(String(item.id))
                ),
            ),
          ];
        }

        relatedItems = applyRelatedContentSectionModes(relatedItems, explicitRelated);
      }

      // Single state update for page data
      setPageData({
        ...data,
        relatedItems // Use generic name
      });

      setMetadata({
        author,
        tags,
        year,
        productId,
        productLink,
        source,
        sourceLinkUrl: firstAbsoluteLegacySourceUrl(parsedMeta),
      });

    } finally {
      setLoading(false);
    }
  };

  const handleUnrelateItem = async (item) => {
    const itemName = item.name || item.title || 'this item';
    if (!window.confirm(`Are you sure you want to remove the relationship with "${itemName}"?`)) {
      return;
    }

    try {
      // Get current parsed metadata
      const currentMeta = typeof pageData.metadata === 'string'
        ? JSON.parse(pageData.metadata || '{}')
        : (pageData.metadata || {});

      const relatedContent = currentMeta.related_content || {};
      const itemId = item.page_id || item.id || item.pageId;

      // Add to exclusion list (so tag-based matches are hidden on reload too)
      const existingExcluded = (relatedContent.excluded || []);
      const alreadyExcluded = existingExcluded.some(e => e.id === itemId);
      const updatedExcluded = alreadyExcluded 
        ? existingExcluded 
        : [...existingExcluded, { id: itemId }];

      const updatedRelatedContent = {
        ...relatedContent,
        // Also remove from explicit lists if it was there
        tunes: (relatedContent.tunes || []).filter(t => t.id !== itemId),
        articles: (relatedContent.articles || []).filter(a => a.id !== itemId),
        excluded: updatedExcluded,
      };

      const updatedMetadata = {
        ...currentMeta,
        related_content: updatedRelatedContent
      };

      // Update the page on the server
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/content/pages/${actualPageId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...pageData,
          metadata: JSON.stringify(updatedMetadata)
        })
      });

      if (!response.ok) throw new Error('Failed to update relationship');

      // Update local state — remove from the UI immediately
      setPageData(prev => ({
        ...prev,
        metadata: JSON.stringify(updatedMetadata),
        relatedItems: prev.relatedItems.filter(i => {
          const iId = i.page_id || i.id || i.pageId;
          return iId !== itemId;
        })
      }));

    } catch (err) {
      console.error('Error removing relationship:', err);
      showError('Failed to remove relationship');
    }
  };

  if (loading) {
    return (
      <div className="page-viewer-container">
        <div className="page-viewer-content">
          <div className="loading">{t.ui.page.loading}</div>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="page-viewer-container">
        <div className="page-viewer-content">
          <div className="error">
            <h2>{t.ui.page.notFoundTitle}</h2>
            <p>{t.ui.page.notFoundText}</p>
          </div>
        </div>
      </div>
    );
  }

  const isArticle = pageData && pageData.pageType === 'article';
  const isEmbeddedArticle = isArticle && isEmbeddedArticlePageHtml(pageData.html);
  const isTune = pageData && (pageData.pageType === 'tune-notation' || pageData.pageType === 'abc-notation');
  const isBookIndex = pageData && pageData.pageType === 'book-index';
  const isCdIndex = pageData && pageData.pageType === 'cd-index';
  const isIndexPage = isBookIndex || isCdIndex;
  const isAbcIntroduction = actualPageId === 'abc-introduction';
  const translatedPage = getTranslatedPage(actualPageId, language);
  const bodyHtml = translatedPage ? translatedPage.html : pageData.html;
  const displayTitle = translatedPage?.title || pageData.title;
  const displayDescription = translatedPage?.description || pageData.description;
  const showPageShare = isArticle || isTune || isAbcIntroduction;
  /** Same “articles” visibility toggle; covers schedule & other /page/… content, not tune pages */
  const showPageViewerContentFeedback =
    actualPageId &&
    actualPageId !== 'abc-introduction' &&
    !isTune &&
    !isIndexPage &&
    !isEmbeddedArticle;

  // Parse metadata from JSON if available
  let parsedMetadata = {};
  if (pageData.metadata) {
    try {
      parsedMetadata = typeof pageData.metadata === 'string'
        ? JSON.parse(pageData.metadata)
        : pageData.metadata;
    } catch (e) {
      console.error('Error parsing metadata:', e);
    }
  }

  // Unified Related grid heading: per-section tune title when only tunes show, else umbrella section_title.
  const rcRel = parsedMetadata?.related_content || {};
  const stTitles = rcRel.section_titles && typeof rcRel.section_titles === 'object' ? rcRel.section_titles : {};
  const itemsForTitle = pageData.relatedItems || [];
  const onlyTunesShowing =
    itemsForTitle.length > 0 &&
    itemsForTitle.every(
      (it) =>
        it.itemType === 'tune' ||
        it.pageType === 'tune-notation' ||
        it.page_type === 'tune-notation'
    );
  const tuneTitle = stTitles.tunes != null && String(stTitles.tunes).trim();
  const umbrellaTitle = rcRel.section_title != null && String(rcRel.section_title).trim();
  let relatedSectionTitle = 'Related';
  if (onlyTunesShowing) {
    relatedSectionTitle = tuneTitle || umbrellaTitle || '🎵 Related Tunes';
  } else {
    relatedSectionTitle = umbrellaTitle || tuneTitle || 'Related';
  }

  const siteBase = 'https://www.cranfordpub.ca';
  let seoDescription =
    displayDescription ||
    `Read ${
      displayTitle ? decodeHtmlEntities(displayTitle) : 'this page'
    } on Cranford Publications - Celtic Fiddle Music`;
  let seoKeywords = metadata.tags?.length
    ? metadata.tags.join(', ')
    : 'celtic music, fiddle, traditional music';
  let seoImage =
    pageData.og_image || `${siteBase}/images/collage9.jpg`;

  if (isTune) {
    seoDescription = buildTuneSeoDescription(
      pageData.title,
      pageData.description,
      parsedMetadata
    );
    seoKeywords = buildTuneSeoKeywords(parsedMetadata, metadata.tags);
    seoImage = resolveTuneOgImageUrl(siteBase, pageData.og_image, parsedMetadata);
  }

  return (
    <>
      <SEO
        title={displayTitle ? `${decodeHtmlEntities(displayTitle)} | Cranford Publications` : 'Cranford Publications'}
        description={seoDescription}
        canonical={`/page/${actualPageId}`}
        keywords={seoKeywords}
        type={isArticle ? 'article' : isTune ? 'music.song' : 'website'}
        image={seoImage}
      />
      <div
        className={`page-viewer-container ${isTune ? 'tune-page-viewer' : ''} ${
          isEmbeddedArticle ? 'page-viewer--embedded-article' : ''
        } ${pageData?.pageType === 'recording' ? 'page-viewer--recording' : ''}`.trim()}
      >
        <div className="page-viewer-content">
          {/* Edit button for admin/content creators */}
          {canEdit && (
            <button
              className="edit-page-btn"
              onClick={() => navigate(`/admin/pages/edit?pageId=${actualPageId}`)}
              title={t.ui.page.editTitle}
            >
              {getIcon('edit')} {t.ui.page.edit}
            </button>
          )}

          {canEdit && pageData.is_hidden && (
            <div className="page-hidden-notice" role="status">
              This page is hidden from the public site.
            </div>
          )}

          {/* Standard Header — index pages embed their own title block in HTML */}
          {actualPageId !== 'abc-introduction' && !isArticle && !isTune && !isIndexPage && (
            <div className="page-header">
              {displayTitle && (
                <h1 className="page-title">{decodeHtmlEntities(displayTitle)}</h1>
              )}

              {/* Enhanced Metadata for Articles */}
              {(isArticle || isTune) && (
                <div className="page-header-meta">
                  {metadata.author && (
                    <span className="header-meta-item">
                      {getIcon('user')} {metadata.author}
                    </span>
                  )}
                  {metadata.year && (
                    <span className="header-meta-item">
                      {getIcon('calendar')} {metadata.year}
                    </span>
                  )}
                  {pageData.pageType && (
                    <span className="header-meta-item">
                      <span className="page-type-badge">{pageData.pageType}</span>
                    </span>
                  )}

                  {/* Source Link */}
                  {metadata.source && metadata.source.startsWith('http') && (
                    <span className="header-meta-item">
                      <a href={metadata.source} target="_blank" rel="noopener noreferrer" className="source-link" title="View Original Source">
                        {getIcon('link')} Source
                      </a>
                    </span>
                  )}
                </div>
              )}

              {displayDescription && !isArticle && !isTune && (
                <p className="page-description">{displayDescription}</p>
              )}
            </div>
          )}


          {/* Back button for articles without embedded template chrome */}
          {isArticle && !isEmbeddedArticle && (
            <div className="page-nav-bar">
              <span />
              <button
                className="back-button"
                onClick={() => navigate(-1)}
                title="Back"
              >
                {getIcon('arrowLeft')}
              </button>
            </div>
          )}

          {/* Floating share button - stays visible when scrolling */}
          {showPageShare && (
            <ShareFloatingButton
              title={displayTitle || (isTune ? parsedMetadata.tune_name : t.ui.share.typeArticle)}
              url={`${window.location.origin}/page/${actualPageId}`}
              text={displayTitle || ''}
              openEmailModal={() => setShowShareModal(true)}
              showSuccess={showSuccess}
            />
          )}

          {/* Tune pages: metadata-driven renderer — no dangerouslySetInnerHTML */}
          {isTune ? (
            <TunePage pageData={pageData} pageId={actualPageId} />
          ) : (
            <div
              className={[
                'page-content',
                isIndexPage ? 'page-content--index-page' : '',
                isEmbeddedArticle
                  ? 'page-content--embedded-article'
                  : isArticle
                    ? 'article-content-standardized'
                    : '',
                isArticle && !isEmbeddedArticle && pageData.articleLayoutWrapperClass
                  ? pageData.articleLayoutWrapperClass
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )}

          {/* Related Items Section (Unified) — articles and non-tune pages only */}
          {!isTune && pageData.relatedItems && pageData.relatedItems.length > 0 &&
           !pageData.html?.includes('<!-- Start of Tune Page Content -->') && (
            <div className="related-products-section">
              <h3>{relatedSectionTitle}</h3>
              <div className="related-products-grid">
                {pageData.relatedItems.map(item => (
                  <RelatedItemCard
                    key={`${item.itemType}-${item.id ?? item.page_id ?? item.pageId ?? 'x'}`}
                    item={item}
                    navigate={navigate}
                    canEdit={canEdit}
                    onUnrelate={handleUnrelateItem}
                  />
                ))}
              </div>
            </div>
          )}

          {showPageViewerContentFeedback && (
            <UserContentFeedback
              targetType="article_page"
              targetId={String(actualPageId)}
              settingKey="user_ratings_articles_enabled"
              title={isArticle ? 'Rate this article' : 'Rate this page'}
            />
          )}

          {/* Metadata footer for articles — skip when HTML includes its own article-page chrome */}
          {isArticle && !isEmbeddedArticle && (
            <div className="page-footer tune-footer">
              <div className="tune-footer-attributes">
                {metadata.author && (
                  <span className="footer-attr">
                    <strong>Author:</strong> {decodeHtmlEntities(metadata.author)}
                  </span>
                )}
                {metadata.year && (
                  <span className="footer-attr">
                    <strong>Published:</strong> {metadata.year}
                  </span>
                )}
                <span className="footer-attr">
                  <strong>Type:</strong> {pageData.pageType}
                </span>
                {metadata.productId && (
                  <span className="footer-attr">
                    <strong>Product:</strong>{' '}
                    {metadata.productLink ? (
                      <a href={metadata.productLink} className="article-footer-link product-link">
                        {metadata.productId}
                      </a>
                    ) : (
                      <span>{metadata.productId}</span>
                    )}
                  </span>
                )}
                {metadata.source && (
                  <span className="footer-attr">
                    <strong>Source:</strong>{' '}
                    {String(metadata.source).startsWith('http') ? (
                      <a
                        href={metadata.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="article-footer-link source-link"
                      >
                        Link
                      </a>
                    ) : (
                      decodeHtmlEntities(String(metadata.source))
                    )}
                  </span>
                )}
              </div>

              {metadata.tags && metadata.tags.length > 0 && (
                <div className="tune-footer-tags">
                  <strong>Tags:</strong>
                  <div className="tags-container">
                    {metadata.tags.map((tag, index) => (
                      <span key={index} className="tag-small">
                        {typeof tag === 'string' ? tag.trim() : tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                className="back-button"
                onClick={() => navigate(-1)}
                title="Back"
              >
                {getIcon('arrowLeft')}
              </button>
            </div>
          )}

          {/* Share Modal for Articles and ABC introduction */}
          {(isArticle || isAbcIntroduction) && (
            <ShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              contentTitle={displayTitle || t.ui.share.typeArticle}
              contentUrl={`${window.location.origin}/page/${actualPageId}`}
              contentId={actualPageId}
              contentType="article"
              contentDescription={displayDescription || ''}
            />
          )}

          {/* Share Modal for Tunes */}
          {isTune && (
            <ShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              contentTitle={pageData.title || parsedMetadata.tune_name || 'Untitled Tune'}
              contentUrl={`${window.location.origin}/page/${actualPageId}`}
              contentId={actualPageId}
              contentType="tune"
              contentDescription={`${parsedMetadata.tune_type || 'Tune'} - ${parsedMetadata.artist || ''}`}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PageViewer;
